import { useState } from "react";
import { Copy, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { reservationEmailHtml } from "../../supabase/functions/_shared/reservation-template";

const confirmationSubject = "Your qwip pod reservation is confirmed";
const confirmationHtml = reservationEmailHtml({
  customerName: "Alex",
  podLabel: "Westfield London",
  dateLabel: "Friday 16 October",
  fromLabel: "2 PM",
  toLabel: "3 PM",
  durationLabel: "1 hour",
  amountLabel: "£15.00",
  accessCode: "•••••",
});

function ConfirmationDraft() {
  const [copied, setCopied] = useState(false);
  return (
    <section className="mb-16" aria-label="Reservation confirmation draft">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-funnel text-2xl font-semibold text-primary">Reservation confirmation</h2>
        <Button variant="outline" size="sm" className="gap-2" onClick={async () => {
          await navigator.clipboard.writeText(confirmationHtml);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        }}>
          {copied ? <Check size={14} /> : <Copy size={14} />}{copied ? "Copied!" : "Copy confirmation HTML"}
        </Button>
      </div>
      <p className="mb-4 break-words text-sm text-foreground">Subject: {confirmationSubject}</p>
      <iframe srcDoc={confirmationHtml} title="Paid reservation confirmation preview" className="h-[1320px] w-full border-0 tablet:h-[1160px]" sandbox="allow-popups allow-popups-to-escape-sandbox" />
    </section>
  );
}

interface Recipient {
  name: string;
  email: string;
  subject: string;
  subjectNote: string;
}

interface ReservationRecipient {
  name: string;
  email: string;
  subject: string;
  subjectNote: string;
  timeSlot: string;
  paymentDeadline: string;
  paymentLink: string;
}

const RECIPIENTS: Recipient[] = [
  {
    name: "Hamza",
    email: "hamzakhawaja058@gmail.com",
    subject: "Hamza, did the pod actually work?",
    subjectNote: "Specific, curious, names them, implies you want the truth",
  },
  {
    name: "Dilz",
    email: "dilzrandhawa@gmail.com",
    subject: "quick favour, Dilz?",
    subjectNote: "Low-key, personal, hard to ignore",
  },
];

const RESERVATION_RECIPIENTS: ReservationRecipient[] = [
  {
    name: "Francesco",
    email: "francesco@example.com",
    subject: "Francesco, your qwip pod is held for tonight",
    subjectNote: "Personal, urgent, confirms held reservation and payment deadline",
    timeSlot: "today, 7 September, 7-8 PM",
    paymentDeadline: "5 PM today (7 September)",
    paymentLink: "https://buy.stripe.com/aFadR9g1xafz97b8j66Ri01",
  },
];

const buildEmailHtml = (name: string) => `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>We'd love your feedback.</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Funnel+Display:wght@400;700&family=Cormorant+Garamond:ital@1&family=DM+Sans:wght@400;500;600;700&display=swap');
    body { margin: 0; padding: 0; background-color: #f2f0ec; font-family: 'DM Sans', Arial, sans-serif; color: #1f3d32; -webkit-font-smoothing: antialiased; }
    .outer { padding: 40px 16px; }
    .card { max-width: 560px; margin: 0 auto; background: #FDFBF7; border-radius: 12px; overflow: hidden; }
    .inner { padding: 44px 40px 36px; }
    .logo { font-family: 'Funnel Display', sans-serif; font-size: 32px; font-weight: 700; color: #1f3d32; text-decoration: none; display: block; margin-bottom: 28px; }
    .divider { height: 1px; background: #c8ddd2; margin-bottom: 32px; }
    h1 { font-family: 'Funnel Display', sans-serif; font-size: 26px; font-weight: 700; margin: 0 0 16px; color: #1f3d32; line-height: 1.25; }
    p { font-size: 15px; line-height: 1.7; color: #3a564a; margin: 0 0 16px; }
    .btn { display: inline-block; background: #1f3d32; color: #ffffff !important; padding: 14px 32px; border-radius: 8px; text-decoration: none; font-weight: 600; font-size: 14px; font-family: 'DM Sans', sans-serif; }
    .secondary-link { display: inline-block; margin-top: 16px; font-size: 14px; color: #1f3d32; text-decoration: underline; }
    .accent { font-style: italic; font-family: 'Cormorant Garamond', Georgia, serif; font-size: 17px; color: #1f3d32; }
    .footer-bar { background: #1f3d32; padding: 24px 40px; }
    .footer-bar p { color: #a3c4b3; font-size: 12px; margin: 0 0 4px; }
    .footer-bar a { color: #a3c4b3; text-decoration: underline; }
    .footer-bar .brand { font-family: 'Funnel Display', sans-serif; color: #ffffff; font-size: 16px; font-weight: 700; margin-bottom: 8px; }
  </style>
</head>
<body>
  <div class="outer">
    <div class="card">
      <div class="inner">
        <span class="logo">qwip</span>
        <div class="divider"></div>
        <h1>We'd love your feedback.</h1>
        <p>Hey ${name},</p>
        <p>Saw you tried out our pod the other day - glad it caught your attention!</p>
        <p>We'd love to hear what you thought. The good, the awkward, the <span class="accent">"this would've been better if..."</span> - all of it helps us build something people actually want to keep using.</p>
        <p>Just hit reply. A real human (one of us) reads every single one, and as a thank you we'll knock a discount off your next session.</p>
        <div style="text-align:center; margin: 32px 0 8px;">
          <a href="mailto:hello@qwip.co.uk?subject=My%20pod%20feedback" class="btn">Reply with feedback</a><br>
          <a href="https://wa.me/447347474059" class="secondary-link">Or message us on WhatsApp</a>
        </div>
      </div>
      <div class="footer-bar">
        <p class="brand">qwip</p>
        <p>the modern public phone booth.</p>
        <p style="margin-top: 12px;"><a href="https://qwip.co.uk">qwip.co.uk</a>&nbsp;&nbsp;·&nbsp;&nbsp;<a href="https://wa.me/447347474059">WhatsApp</a></p>
      </div>
    </div>
  </div>
</body>
</html>`;

const buildReservationEmailHtml = (
  name: string,
  timeSlot: string,
  paymentDeadline: string,
  paymentLink: string
) => `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Your qwip reservation</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Funnel+Display:wght@400;700&family=Cormorant+Garamond:ital@1&family=DM+Sans:wght@400;500;600;700&display=swap');
    body { margin: 0; padding: 0; background-color: #f2f0ec; font-family: 'DM Sans', Arial, sans-serif; color: #1f3d32; -webkit-font-smoothing: antialiased; }
    .outer { padding: 40px 16px; }
    .card { max-width: 560px; margin: 0 auto; background: #FDFBF7; border-radius: 12px; overflow: hidden; }
    .inner { padding: 44px 40px 36px; }
    .logo { font-family: 'Funnel Display', sans-serif; font-size: 32px; font-weight: 700; color: #1f3d32; text-decoration: none; display: block; margin-bottom: 28px; }
    .divider { height: 1px; background: #c8ddd2; margin-bottom: 32px; }
    h1 { font-family: 'Funnel Display', sans-serif; font-size: 26px; font-weight: 700; margin: 0 0 16px; color: #1f3d32; line-height: 1.25; }
    p { font-size: 15px; line-height: 1.7; color: #3a564a; margin: 0 0 16px; }
    .btn { display: inline-block; background: #1f3d32; color: #ffffff !important; padding: 14px 32px; border-radius: 8px; text-decoration: none; font-weight: 600; font-size: 14px; font-family: 'DM Sans', sans-serif; }
    .secondary-link { display: inline-block; margin-top: 16px; font-size: 14px; color: #1f3d32; text-decoration: underline; }
    .accent { font-style: italic; font-family: 'Cormorant Garamond', Georgia, serif; font-size: 17px; color: #1f3d32; }
    .footer-bar { background: #1f3d32; padding: 24px 40px; }
    .footer-bar p { color: #a3c4b3; font-size: 12px; margin: 0 0 4px; }
    .footer-bar a { color: #a3c4b3; text-decoration: underline; }
    .footer-bar .brand { font-family: 'Funnel Display', sans-serif; color: #ffffff; font-size: 16px; font-weight: 700; margin-bottom: 8px; }
  </style>
</head>
<body>
  <div class="outer">
    <div class="card">
      <div class="inner">
        <span class="logo">qwip</span>
        <div class="divider"></div>
        <h1>Your pod is held for tonight.</h1>
        <p>Hi ${name},</p>
        <p>We've held your reservation for <strong>${timeSlot}</strong>.</p>
        <p>Please complete payment by <strong>${paymentDeadline}</strong> to keep your booking confirmed.</p>
        <div style="text-align:center; margin: 24px 0 8px;">
          <a href="${paymentLink}" class="btn" target="_blank" rel="noopener noreferrer">Complete payment</a>
        </div>
        <p>Once your payment is confirmed, you'll receive a confirmation email with your access code and full entry instructions. Just tap the code into the door handle when you arrive.</p>
        <p>Need help? <a href="https://wa.me/447347474059" class="secondary-link">Message us on WhatsApp</a>.</p>
      </div>
      <div class="footer-bar">
        <p class="brand">qwip</p>
        <p>the modern public phone booth.</p>
        <p style="margin-top: 12px;"><a href="https://qwip.co.uk">qwip.co.uk</a>&nbsp;&nbsp;-&nbsp;&nbsp;<a href="https://wa.me/447347474059">WhatsApp</a></p>
      </div>
    </div>
  </div>
</body>
</html>`;

const RecipientCard = ({ recipient }: { recipient: Recipient }) => {
  const [copiedHtml, setCopiedHtml] = useState(false);
  const [copiedSubject, setCopiedSubject] = useState(false);
  const html = buildEmailHtml(recipient.name);

  const copy = async (text: string, kind: "html" | "subject") => {
    await navigator.clipboard.writeText(text);
    if (kind === "html") {
      setCopiedHtml(true);
      setTimeout(() => setCopiedHtml(false), 2000);
    } else {
      setCopiedSubject(true);
      setTimeout(() => setCopiedSubject(false), 2000);
    }
  };

  return (
    <section className="mb-16">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <h2 className="font-funnel text-2xl font-semibold text-[#1f3d32]">
          Email to {recipient.name}
        </h2>
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5 border-[#1f3d32]/20 text-[#1f3d32] hover:bg-[#1f3d32] hover:text-[#FDFBF7]"
          onClick={() => copy(html, "html")}
        >
          {copiedHtml ? <Check size={14} /> : <Copy size={14} />}
          {copiedHtml ? "Copied!" : "Copy HTML"}
        </Button>
      </div>

      <div className="mb-3 text-sm text-[#3a564a] bg-white border border-[#1f3d32]/15 rounded-lg p-4">
        <div className="grid grid-cols-[80px_1fr] gap-y-1.5 gap-x-3 items-center">
          <span className="text-[#1f3d32]/60 font-medium">From</span>
          <span className="font-mono">qwip &lt;hello@qwip.co.uk&gt;</span>
          <span className="text-[#1f3d32]/60 font-medium">To</span>
          <span className="font-mono">{recipient.email}</span>
          <span className="text-[#1f3d32]/60 font-medium">Subject</span>
          <span className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-[#1f3d32] font-semibold">
              {recipient.subject}
            </span>
            <button
              onClick={() => copy(recipient.subject, "subject")}
              className="text-[#1f3d32]/60 hover:text-[#1f3d32] transition-colors p-1"
              aria-label="Copy subject"
            >
              {copiedSubject ? <Check size={14} /> : <Copy size={14} />}
            </button>
          </span>
        </div>
        <p className="text-xs text-[#3a564a]/70 mt-3 pt-3 border-t border-[#1f3d32]/10">
          {recipient.subjectNote}
        </p>
      </div>

      <div className="rounded-xl overflow-hidden border border-[#1f3d32]/15 bg-[#f2f0ec]">
        <iframe
          srcDoc={html}
          title={`Email preview for ${recipient.name}`}
          className="w-full border-0"
          style={{ height: "780px" }}
          sandbox=""
        />
      </div>
    </section>
  );
};

const ReservationCard = ({ recipient }: { recipient: ReservationRecipient }) => {
  const [copiedHtml, setCopiedHtml] = useState(false);
  const [copiedSubject, setCopiedSubject] = useState(false);
  const html = buildReservationEmailHtml(
    recipient.name,
    recipient.timeSlot,
    recipient.paymentDeadline,
    recipient.paymentLink
  );

  const copy = async (text: string, kind: "html" | "subject") => {
    await navigator.clipboard.writeText(text);
    if (kind === "html") {
      setCopiedHtml(true);
      setTimeout(() => setCopiedHtml(false), 2000);
    } else {
      setCopiedSubject(true);
      setTimeout(() => setCopiedSubject(false), 2000);
    }
  };

  return (
    <section className="mb-16">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <h2 className="font-funnel text-2xl font-semibold text-[#1f3d32]">
          Reservation email to {recipient.name}
        </h2>
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5 border-[#1f3d32]/20 text-[#1f3d32] hover:bg-[#1f3d32] hover:text-[#FDFBF7]"
          onClick={() => copy(html, "html")}
        >
          {copiedHtml ? <Check size={14} /> : <Copy size={14} />}
          {copiedHtml ? "Copied!" : "Copy HTML"}
        </Button>
      </div>

      <div className="mb-3 text-sm text-[#3a564a] bg-white border border-[#1f3d32]/15 rounded-lg p-4">
        <div className="grid grid-cols-[80px_1fr] gap-y-1.5 gap-x-3 items-center">
          <span className="text-[#1f3d32]/60 font-medium">From</span>
          <span className="font-mono">qwip &lt;hello@qwip.co.uk&gt;</span>
          <span className="text-[#1f3d32]/60 font-medium">To</span>
          <span className="font-mono">{recipient.email}</span>
          <span className="text-[#1f3d32]/60 font-medium">Subject</span>
          <span className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-[#1f3d32] font-semibold">
              {recipient.subject}
            </span>
            <button
              onClick={() => copy(recipient.subject, "subject")}
              className="text-[#1f3d32]/60 hover:text-[#1f3d32] transition-colors p-1"
              aria-label="Copy subject"
            >
              {copiedSubject ? <Check size={14} /> : <Copy size={14} />}
            </button>
          </span>
        </div>
        <p className="text-xs text-[#3a564a]/70 mt-3 pt-3 border-t border-[#1f3d32]/10">
          {recipient.subjectNote}
        </p>
      </div>

      <div className="rounded-xl overflow-hidden border border-[#1f3d32]/15 bg-[#f2f0ec]">
        <iframe
          srcDoc={html}
          title={`Reservation email preview for ${recipient.name}`}
          className="w-full border-0"
          style={{ height: "780px" }}
          sandbox=""
        />
      </div>
    </section>
  );
};

const DraftEmail = () => {
  return (
    <div className="min-h-screen bg-[#FDFBF7] py-16 px-6">
      <div className="max-w-3xl mx-auto">
        <header className="mb-12">
          <p className="text-sm uppercase tracking-widest text-[#1f3d32]/60 font-medium mb-3">
            Email drafts
          </p>
          <h1 className="font-funnel text-4xl tablet:text-5xl font-bold text-[#1f3d32] mb-4">
            Ready-to-send emails
          </h1>
          <p className="text-base text-[#3a564a] max-w-xl">
            Copy the HTML for each email and paste it into your email client.
            Send each one individually so recipients don't see each other.
          </p>
        </header>

        <ConfirmationDraft />

        <div className="mb-16">
          <h2 className="font-funnel text-2xl font-semibold text-[#1f3d32] mb-6">
            Pod feedback
          </h2>
          {RECIPIENTS.map((r) => (
            <RecipientCard key={r.email} recipient={r} />
          ))}
        </div>

        <div>
          <h2 className="font-funnel text-2xl font-semibold text-[#1f3d32] mb-6">
            Reserved booking
          </h2>
          {RESERVATION_RECIPIENTS.map((r) => (
            <ReservationCard key={r.email} recipient={r} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default DraftEmail;
